/**
 * Ordering Content Type — Player
 * Title + description, optional lesson (Page), then one or more ordering exercises
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import type { GradingResult } from '@ai-content-hub/content-kit';
import { CompositePlayerGroupedItemsBody, QuestionCounterLabel } from '@ai-content-hub/content-core';
import {
    OrderingPlayer as OrderingElementPlayer,
    type OrderingAnswer,
    PagePlayer,
} from '@ai-content-hub/content-elements';
import { isCorrectAnswerRevealed, useLocale } from '@ai-content-hub/content-core';
import type { OrderingContentData, OrderingContentConfig } from './schema';
import { normalizeOrderingContentData } from './schema';
import type { OrderingContentExercise } from './schema';
import type { OrderingContentCompositeAnswer } from './compositeGrading';
import { normalizeOrderingContentAnswer } from './compositeGrading';
import { FileText } from 'lucide-react';
import map from 'lodash/map';

export interface OrderingContentPlayerProps {
    data?: OrderingContentData;
    value?: OrderingContentData;
    config?: OrderingContentConfig;
    /** Per-exercise map by exercise id, or legacy single ordering answer */
    answer?: OrderingAnswer | OrderingContentCompositeAnswer;
    onAnswer?: (answer: OrderingContentCompositeAnswer) => void;
    isReview?: boolean;
    disabled?: boolean;
    showFeedback?: boolean;
    result?: GradingResult;
    className?: string;
}

export const OrderingContentPlayer: React.FC<OrderingContentPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    config,
    answer,
    onAnswer,
    isReview = false,
    disabled = false,
    showFeedback = false,
    result,
    className,
}) => {
    const data = dataProp ?? valueProp;

    const safeData = normalizeOrderingContentData(data);
    const { t } = useLocale({ config: config?.i18n });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const showCounter = safeData.exercises.length > 1;


    const hasDescription = Boolean(safeData.description?.trim());
    const hasLessonContent = (safeData.lessonContent?.sections?.length ?? 0) > 0;

    const exerciseIds = React.useMemo(() => map(safeData.exercises, (e) => e.id), [safeData.exercises]);
    const answersByEx = normalizeOrderingContentAnswer(answer, exerciseIds);

    const exerciseById = React.useMemo(
        () => new Map(map(safeData.exercises, (e) => [e.id, e] as const)),
        [safeData.exercises]
    );

    const handleSubAnswer = (exId: string, next: OrderingAnswer) => {
        onAnswer?.({ ...answersByEx, [exId]: next });
    };

    const renderExercise = (ex: OrderingContentExercise, index: number) => (
        <div
            key={ex.id}
            className="ordering-content-player-exercise border-border border-b border-dashed pb-8 last:border-b-0 last:pb-0"
        >
            <div className={cn('ordering-content-player-exercise-inner', showCounter && 'flex gap-3 items-start')}>
                {showCounter && <QuestionCounterLabel index={index} className="mt-1" />}
                <div className="min-w-0 flex-1">
                    <OrderingElementPlayer
                        data={ex.data}
                        answer={answersByEx[ex.id] ?? []}
                        onAnswer={(next) => handleSubAnswer(ex.id, next)}
                        isReview={isReview}
                        disabled={disabled}
                        showFeedback={showFeedback}
                        result={safeData.exercises.length === 1 ? result : undefined}
                        config={config as Record<string, unknown>}
                    />
                </div>
            </div>
        </div>
    );

    const body =
        exerciseIds.length === 0 ? null : (
            <CompositePlayerGroupedItemsBody
                prefix="ordering-content-player"
                itemIds={exerciseIds}
                questionsSidebarRows={safeData.questionsSidebarRows}
                renderItem={(id) => {
                    const ex = exerciseById.get(id);
                    if (!ex) return null;
                    const idx = exerciseIds.indexOf(id);
                    return renderExercise(ex, idx === -1 ? 0 : idx);
                }}
            />
        );

    return (
        <div className={cn('ordering-content-player flex flex-col gap-4', className)}>
            {hasDescription && (
                <div className="ordering-content-player-header">
                    <p className="ordering-content-player-description text-sm text-muted-foreground leading-relaxed">
                        {safeData.description}
                    </p>
                </div>
            )}

            {hasLessonContent && (
                <div className="ordering-content-player-lesson">
                    <div className="ordering-content-player-lesson-header flex items-center gap-2 pb-3 border-b border-border mb-3">
                        <FileText className="h-5 w-5 text-primary shrink-0" aria-hidden />
                        <h3 className="font-semibold text-foreground">{t('contentTab', 'Content')}</h3>
                    </div>
                    <PagePlayer data={safeData.lessonContent} submitted={showFeedback && revealCorrect} />
                </div>
            )}

            <div className="ordering-content-player-body flex flex-col gap-8">{body}</div>
        </div>
    );
};

export default OrderingContentPlayer;
